import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { DetectionRegion, ExportOptions, ImageJob, ProcessingOptions } from './types'
import { createId } from './core/id'
import { pushHistory, redoHistory, undoHistory } from './core/history'
import { createPreviewDecoded, decodeImageFile, drawDecodedToCanvas } from './services/decodeImage'
import { detectFaces } from './services/faceDetector'
import { detectJapanesePii } from './services/ocrDetector'
import { detectBarcodes } from './services/barcodeDetector'
import { createBatchZipForJobs, exportJob, shareFile, triggerDownload, type ExportResult } from './services/exportImage'
import { getRuntimeStatus, registerServiceWorker } from './services/runtime'
import { takeSharedFiles } from './services/sharedFiles'
import { UploadZone } from './components/UploadZone'
import { QueueSidebar } from './components/QueueSidebar'
import { EditorCanvas } from './components/EditorCanvas'
import { RegionList } from './components/RegionList'
import { ComparisonPreview } from './components/ComparisonPreview'

const defaultProcessing: ProcessingOptions = {
  detectFaces: true,
  detectText: true,
  detectBarcodes: true,
  thoroughFaceScan: true,
  maxLongEdge: 2600,
}

const defaultExport: ExportOptions = {
  mode: 'solid',
  fillColor: '#0b1f33',
  mime: 'image/jpeg',
  quality: 0.94,
  appendSafeSuffix: true,
}

const MAX_QUEUE_IMAGES = 8
const MAX_FILE_BYTES = 80 * 1024 * 1024

type MobilePanel = 'assets' | 'regions' | 'finish' | null

function ToolbarIcon({ name }: { name: 'images' | 'undo' | 'redo' | 'download' }) {
  const paths = {
    images: <><rect x="4" y="5" width="13" height="13" rx="1.5" /><path d="m7 14 3-3 2.2 2.2 1.8-1.8 3 3" /><path d="M8 5V3.8A1.8 1.8 0 0 1 9.8 2h8.4A1.8 1.8 0 0 1 20 3.8v8.4a1.8 1.8 0 0 1-1.8 1.8H17" /></>,
    undo: <><path d="M9 7 4.5 11.5 9 16" /><path d="M5 11.5h7.5a6 6 0 0 1 6 6" /></>,
    redo: <><path d="m15 7 4.5 4.5L15 16" /><path d="M19 11.5h-7.5a6 6 0 0 0-6 6" /></>,
    download: <><path d="M12 3v11" /><path d="m7.5 10 4.5 4.5 4.5-4.5" /><path d="M5 20h14" /></>,
  }

  return <svg className="toolbar-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{paths[name]}</svg>
}

function initialJob(file: File): ImageJob {
  return {
    id: createId('image'),
    file,
    objectUrl: URL.createObjectURL(file),
    name: file.name || `image-${Date.now()}.jpg`,
    phase: 'idle',
    phaseLabel: '処理を待っています',
    progress: 0,
    regions: [],
    history: [[]],
    historyIndex: 0,
    reviewConfirmed: false,
  }
}

function mergeRegions(regions: DetectionRegion[]): DetectionRegion[] {
  const key = (region: DetectionRegion) => `${region.category}:${Math.round(region.rect.x / 10)}:${Math.round(region.rect.y / 10)}:${Math.round(region.rect.width / 10)}:${Math.round(region.rect.height / 10)}`
  const map = new Map<string, DetectionRegion>()
  for (const region of regions) {
    const current = map.get(key(region))
    if (!current || region.confidence > current.confidence) map.set(key(region), region)
  }
  return [...map.values()].sort((a, b) => a.rect.y - b.rect.y || a.rect.x - b.rect.x)
}

function supportsCanvasMime(mime: string): boolean {
  const canvas = document.createElement('canvas')
  canvas.width = 1
  canvas.height = 1
  return canvas.toDataURL(mime).startsWith(`data:${mime}`)
}

export default function App() {
  const [jobs, setJobs] = useState<ImageJob[]>([])
  const [selectedId, setSelectedId] = useState<string>()
  const [processingOptions] = useState(defaultProcessing)
  const [exportOptions, setExportOptions] = useState(defaultExport)
  const [manualMode, setManualMode] = useState(false)
  const [zoom, setZoom] = useState(100)
  const [showOriginal, setShowOriginal] = useState(false)
  const [globalMessage, setGlobalMessage] = useState<string>()
  const [mobilePanel, setMobilePanel] = useState<MobilePanel>(null)
  const [showFinish, setShowFinish] = useState(false)
  const [savePreview, setSavePreview] = useState<{ url: string; filename: string; blob: Blob }>()
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 820px)').matches)
  const processingTail = useRef<Promise<void>>(Promise.resolve())
  const jobsRef = useRef<ImageJob[]>([])
  const cancelledIds = useRef(new Set<string>())
  const manualReviewIds = useRef(new Set<string>())
  const runtime = useMemo(() => getRuntimeStatus(), [])

  const isAppleMobile = useMemo(() => {
    if (typeof navigator === 'undefined') return false
    return /iPad|iPhone|iPod/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  }, [])
  const selected = jobs.find((job) => job.id === selectedId)


  useEffect(() => {
    return () => {
      if (savePreview?.url) URL.revokeObjectURL(savePreview.url)
    }
  }, [savePreview?.url])

  useEffect(() => { jobsRef.current = jobs }, [jobs])
  useEffect(() => { setManualMode(false); setZoom(100); setShowOriginal(false); setMobilePanel(null); setShowFinish(false) }, [selectedId])

  useEffect(() => {
    const query = window.matchMedia('(max-width: 820px)')
    const update = () => setIsMobile(query.matches)
    update()
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])

  useEffect(() => {
    if (!isMobile) { setMobilePanel(null); return }
    if (!mobilePanel) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') setMobilePanel(null) }
    window.addEventListener('keydown', closeOnEscape)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', closeOnEscape)
    }
  }, [isMobile, mobilePanel])

  useEffect(() => {
    if (!showFinish) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') setShowFinish(false) }
    window.addEventListener('keydown', closeOnEscape)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', closeOnEscape)
    }
  }, [showFinish])

  const updateJob = useCallback((id: string, updater: (job: ImageJob) => ImageJob) => {
    setJobs((current) => current.map((job) => job.id === id ? updater(job) : job))
  }, [])

  useEffect(() => {
    setJobs((current) => {
      const next = current.map((job) => ({
        ...job,
        reviewConfirmed: false,
        outputBlob: undefined,
        outputMime: undefined,
        metadataCheck: undefined,
        phase: job.decoded ? 'review' as const : job.phase,
      }))
      jobsRef.current = next
      return next
    })
  }, [exportOptions])

  const processJob = useCallback(async (seed: ImageJob) => {
    const warnings: string[] = []
    let decoded: Awaited<ReturnType<typeof decodeImageFile>> | undefined
    let analysisCanvas: HTMLCanvasElement | undefined
    const releaseProcessingResources = () => {
      if (analysisCanvas) {
        analysisCanvas.width = 1
        analysisCanvas.height = 1
        analysisCanvas = undefined
      }
      decoded?.release()
      decoded = undefined
    }
    try {
      updateJob(seed.id, (job) => ({ ...job, phase: 'decoding', phaseLabel: '画像を端末内で読み込んでいます', progress: 0.04 }))
      decoded = await decodeImageFile(seed.file)
      if (cancelledIds.current.has(seed.id)) {
        decoded.release()
        return
      }
      updateJob(seed.id, (job) => ({ ...job, decoded, progress: 0.12 }))

      const faceInput = drawDecodedToCanvas(decoded, 1900)
      analysisCanvas = faceInput.canvas
      const detected: DetectionRegion[] = []
      const finishReview = (stoppedEarly = false) => {
        if (!decoded) throw new Error('編集用画像を準備できませんでした')
        const regions = mergeRegions(detected)
        const preview = createPreviewDecoded(decoded)
        releaseProcessingResources()
        if (stoppedEarly) warnings.push('自動検出を途中で終了しました。画像全体を手動で確認してください。')
        manualReviewIds.current.delete(seed.id)
        updateJob(seed.id, (job) => ({
          ...job,
          decoded: preview,
          phase: 'review',
          phaseLabel: '検出結果を確認してください',
          progress: 1,
          regions,
          history: [regions.map((region) => ({ ...region, rect: { ...region.rect } }))],
          historyIndex: 0,
          warnings: [...new Set(warnings)],
          reviewConfirmed: false,
        }))
      }
      const shouldStopDetection = () => manualReviewIds.current.has(seed.id)

      if (shouldStopDetection()) {
        finishReview(true)
        return
      }

      if (processingOptions.detectFaces) {
        updateJob(seed.id, (job) => ({ ...job, phase: 'face', phaseLabel: '顔の候補を端末内で探しています', progress: 0.18 }))
        try {
          detected.push(...await detectFaces(
            faceInput.canvas,
            decoded.width,
            decoded.height,
            faceInput.scale,
            processingOptions.thoroughFaceScan,
            (label) => updateJob(seed.id, (job) => manualReviewIds.current.has(seed.id) ? job : ({ ...job, phaseLabel: label })),
          ))
        } catch (error) {
          warnings.push(`顔検出を完了できませんでした: ${error instanceof Error ? error.message : '不明なエラー'}`)
        }
        if (cancelledIds.current.has(seed.id)) { releaseProcessingResources(); return }
        if (shouldStopDetection()) {
          finishReview(true)
          return
        }
      }

      if (processingOptions.detectBarcodes) {
        updateJob(seed.id, (job) => ({ ...job, phase: 'barcode', phaseLabel: 'QRコード・バーコードを確認しています', progress: 0.42 }))
        try {
          detected.push(...await detectBarcodes(faceInput.canvas, decoded.width, decoded.height, faceInput.scale))
        } catch (error) {
          warnings.push(`コード検出を完了できませんでした: ${error instanceof Error ? error.message : '不明なエラー'}`)
        }
        if (cancelledIds.current.has(seed.id)) { releaseProcessingResources(); return }
        if (shouldStopDetection()) {
          finishReview(true)
          return
        }
      }

      if (processingOptions.detectText) {
        updateJob(seed.id, (job) => ({ ...job, phase: 'ocr', phaseLabel: '日本語OCRを準備しています', progress: 0.5 }))
        try {
          const ocrInput = drawDecodedToCanvas(decoded, processingOptions.maxLongEdge)
          try {
            detected.push(...await detectJapanesePii(
              ocrInput.canvas,
              decoded.width,
              decoded.height,
              ocrInput.scale,
              (progress, label) => updateJob(seed.id, (job) => manualReviewIds.current.has(seed.id) ? ({ ...job, progress: 0.5 + progress * 0.43 }) : ({ ...job, phaseLabel: label, progress: 0.5 + progress * 0.43 })),
            ))
          } finally {
            if (ocrInput.canvas !== analysisCanvas) {
              ocrInput.canvas.width = 1
              ocrInput.canvas.height = 1
            }
          }
        } catch (error) {
          warnings.push(`日本語OCRを完了できませんでした: ${error instanceof Error ? error.message : '不明なエラー'}`)
        }
        if (cancelledIds.current.has(seed.id)) { releaseProcessingResources(); return }
        if (shouldStopDetection()) {
          finishReview(true)
          return
        }
      }

      if (cancelledIds.current.has(seed.id)) { releaseProcessingResources(); return }
      finishReview(false)
    } catch (error) {
      releaseProcessingResources()
      manualReviewIds.current.delete(seed.id)
      updateJob(seed.id, (job) => ({
        ...job,
        decoded: undefined,
        phase: 'error',
        phaseLabel: '画像を処理できませんでした',
        progress: 1,
        error: error instanceof Error ? error.message : '不明なエラー',
        warnings,
      }))
    }
  }, [processingOptions, updateJob])

  const addFiles = useCallback((files: File[]) => {
    const validImages = files.filter((file) => file.size > 0 && file.size <= MAX_FILE_BYTES && (file.type.startsWith('image/') || /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)))
    const remainingSlots = Math.max(0, MAX_QUEUE_IMAGES - jobsRef.current.length)
    const accepted = validImages.slice(0, remainingSlots)
    if (!accepted.length) {
      setGlobalMessage(remainingSlots === 0
        ? `同時に扱える画像は${MAX_QUEUE_IMAGES}枚までです`
        : '対応する画像が見つかりませんでした。1枚80MB以下の画像を選択してください。')
      return
    }
    const newJobs = accepted.map(initialJob)
    jobsRef.current = [...jobsRef.current, ...newJobs]
    setJobs(jobsRef.current)
    setSelectedId((current) => current ?? newJobs[0]?.id)
    const omitted = files.length - accepted.length
    setGlobalMessage(omitted > 0 ? `${accepted.length}枚を追加しました。上限・形式・容量により${omitted}枚は追加していません。` : undefined)
    for (const job of newJobs) {
      cancelledIds.current.delete(job.id)
      processingTail.current = processingTail.current.then(() => processJob(job)).catch((error) => console.error(error))
    }
  }, [processJob])

  useEffect(() => {
    registerServiceWorker().catch((error) => console.warn('Service worker registration failed', error))
    const params = new URLSearchParams(window.location.search)
    if (params.get('shareError') === '1') {
      setGlobalMessage('共有メニューから画像を受け取れませんでした。ブラウザで直接画像を選択してください。')
    }
    takeSharedFiles()
      .then((files) => { if (files.length) addFiles(files) })
      .catch((error) => setGlobalMessage(error instanceof Error ? error.message : '共有画像を読み込めませんでした'))
  }, [addFiles])

  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const files = [...(event.clipboardData?.files ?? [])].filter((file) => file.type.startsWith('image/') || /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name))
      if (files.length) { event.preventDefault(); addFiles(files) }
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [addFiles])

  useEffect(() => () => {
    for (const job of jobsRef.current) cancelledIds.current.add(job.id)
    for (const job of jobsRef.current) {
      URL.revokeObjectURL(job.objectUrl)
      job.decoded?.release()
    }
  }, [])

  const removeJob = (id: string) => {
    cancelledIds.current.add(id)
    manualReviewIds.current.delete(id)
    const target = jobs.find((job) => job.id === id)
    if (target) {
      URL.revokeObjectURL(target.objectUrl)
      target.decoded?.release()
    }
    const remaining = jobs.filter((job) => job.id !== id)
    jobsRef.current = remaining
    setJobs(remaining)
    if (selectedId === id) setSelectedId(remaining[0]?.id)
  }

  const commitRegions = (regions: DetectionRegion[]) => {
    if (!selected) return
    updateJob(selected.id, (job) => {
      const next = pushHistory(job.history, job.historyIndex, regions)
      return { ...job, regions, history: next.history, historyIndex: next.index, selectedRegionId: regions.some((r) => r.id === job.selectedRegionId) ? job.selectedRegionId : undefined, outputBlob: undefined, outputMime: undefined, metadataCheck: undefined, error: undefined, phase: 'review', reviewConfirmed: false }
    })
  }

  const undo = () => {
    if (!selected) return
    updateJob(selected.id, (job) => {
      const result = undoHistory(job.history, job.historyIndex)
      return result ? { ...job, regions: result.regions, historyIndex: result.index, phase: 'review', outputBlob: undefined, outputMime: undefined, metadataCheck: undefined, error: undefined, reviewConfirmed: false } : job
    })
  }

  const redo = () => {
    if (!selected) return
    updateJob(selected.id, (job) => {
      const result = redoHistory(job.history, job.historyIndex)
      return result ? { ...job, regions: result.regions, historyIndex: result.index, phase: 'review', outputBlob: undefined, outputMime: undefined, metadataCheck: undefined, error: undefined, reviewConfirmed: false } : job
    })
  }

  const requestManualReview = () => {
    if (!selected?.decoded || manualReviewIds.current.has(selected.id) || !['face', 'ocr', 'barcode'].includes(selected.phase)) return
    manualReviewIds.current.add(selected.id)
    // Do not terminate Tesseract while recognize() is still running. WebKit can
    // otherwise leave the worker bridge without a target and reject on postMessage.
    // The current detector finishes safely, then processJob observes this flag and
    // moves directly to manual review without starting another detector.
    updateJob(selected.id, (job) => ({
      ...job,
      phaseLabel: '現在の検出が終わり次第、手動確認へ進みます',
    }))
  }

  const createCurrentExport = async () => {
    if (!selected?.reviewConfirmed) return
    updateJob(selected.id, (job) => ({
      ...job,
      phase: 'exporting',
      phaseLabel: '完成画像を作成しています',
      progress: 0.5,
    }))

    try {
      const result = await exportJob(selected, exportOptions)
      updateJob(selected.id, (job) => ({
        ...job,
        phase: 'done',
        phaseLabel: '完成画像を作成しました',
        progress: 1,
        outputBlob: result.blob,
        outputMime: result.blob.type,
        metadataCheck: result.inspection,
        error: undefined,
      }))
      return result
    } catch (error) {
      updateJob(selected.id, (job) => ({
        ...job,
        phase: 'review',
        phaseLabel: '保存前の確認が必要です',
        progress: 1,
        error: error instanceof Error
          ? error.message
          : '完成画像を作成できませんでした',
      }))
      return undefined
    }
  }

  const saveCurrent = async () => {
    const result = await createCurrentExport()
    if (!result || !selected) return
    triggerDownload(result.blob, result.filename)
    updateJob(selected.id, (job) => ({
      ...job,
      phaseLabel: '画像を保存しました',
    }))
  }

  const previewCurrent = async () => {
    const result = await createCurrentExport()
    if (!result) return

    setSavePreview({
      url: URL.createObjectURL(result.blob),
      filename: result.filename,
      blob: result.blob,
    })
  }

  const savePreviewFile = () => {
    if (!savePreview) return
    triggerDownload(savePreview.blob, savePreview.filename)
  }

  const sharePreviewCurrent = async () => {
    if (!savePreview) return

    const file = new File(
      [savePreview.blob],
      savePreview.filename,
      { type: savePreview.blob.type || 'image/png' },
    )

    try {
      if (!navigator.share || !navigator.canShare?.({ files: [file] })) {
        triggerDownload(savePreview.blob, savePreview.filename)
        return
      }

      await navigator.share({
        files: [file],
        title: 'かくして共有',
      })
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      setGlobalMessage(
        error instanceof Error
          ? error.message
          : '共有メニューを開けませんでした',
      )
    }
  }

  const saveBatch = async () => {
    const candidates = jobs.filter((job) => job.decoded && job.reviewConfirmed && ['review', 'done'].includes(job.phase))
    if (!candidates.length) {
      setGlobalMessage('ZIP保存する画像がありません。各画像を開き、最終確認のチェックを入れてください。')
      return
    }
    setGlobalMessage('複数画像を再生成しています')
    try {
      const zip = await createBatchZipForJobs(candidates, exportOptions, 120 * 1024 * 1024, (completed, total) => {
        setGlobalMessage(`複数画像を再生成しています（${completed}/${total}）`)
      })
      triggerDownload(zip, `安全確認済み画像_${new Date().toISOString().slice(0, 10)}.zip`)
      setGlobalMessage(`${candidates.length}枚をZIPで保存しました`)
    } catch (error) {
      setGlobalMessage(error instanceof Error ? error.message : '一括保存に失敗しました')
    }
  }

  const processing = selected && ['idle', 'decoding', 'face', 'ocr', 'barcode', 'exporting'].includes(selected.phase)
  const enabledRegionCount = selected?.regions.filter((region) => region.enabled).length ?? 0
  const selectedIndex = selected ? jobs.findIndex((job) => job.id === selected.id) : -1

  const renderFinishPanel = (mobile = false) => selected ? (
    <section className={`finish-panel ${mobile ? 'mobile-finish-panel' : ''}`} aria-labelledby={mobile ? 'mobile-finish-title' : 'finish-title'}>
      <div className="finish-heading">
        <span>保存前の確認</span>
        <h2 id={mobile ? 'mobile-finish-title' : 'finish-title'}>画像全体を見てください</h2>
        <p>隠したい文字や顔が残っていないか確認してから保存します。</p>
      </div>

      <fieldset className="mode-selector">
        <legend>新しく追加する場所の隠し方</legend>
        <div>
          {([
            ['solid', '黒塗り'], ['mosaic', 'モザイク'], ['blur', 'ぼかし'], ['fill', '色で隠す'],
          ] as const).map(([value, title]) => (
            <label key={value} className={exportOptions.mode === value ? 'selected' : ''}>
              <input type="radio" name={mobile ? 'mobile-mode' : 'mode'} value={value} checked={exportOptions.mode === value} onChange={() => setExportOptions((current) => ({ ...current, mode: value }))} />
              <span>{title}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {exportOptions.mode === 'fill' && <label className="compact-row"><span>塗りつぶし色</span><input type="color" value={exportOptions.fillColor} onChange={(event) => setExportOptions((current) => ({ ...current, fillColor: event.target.value }))} /></label>}

      <details className="save-options">
        <summary>保存形式と画質</summary>
        <div>
          <label className="compact-row"><span>形式</span><select value={exportOptions.mime} onChange={(event) => setExportOptions((current) => ({ ...current, mime: event.target.value as ExportOptions['mime'] }))}><option value="image/jpeg">JPEG</option><option value="image/png">PNG</option>{supportsCanvasMime('image/webp') && <option value="image/webp">WebP</option>}</select></label>
          {exportOptions.mime !== 'image/png' && <label className="compact-row"><span>画質</span><input type="range" min="0.72" max="1" step="0.01" value={exportOptions.quality} onChange={(event) => setExportOptions((current) => ({ ...current, quality: Number(event.target.value) }))} /><output>{Math.round(exportOptions.quality * 100)}%</output></label>}
        </div>
      </details>

      <details className="compare-disclosure">
        <summary>隠す前と後を見る</summary>
        <ComparisonPreview decoded={selected.decoded!} regions={selected.regions} options={exportOptions} />
      </details>

      <p className="safety-note"><strong>文字やQRコードは、黒塗りが最も確実です。</strong></p>

      <label className="final-check">
        <input type="checkbox" checked={selected.reviewConfirmed} onChange={(event) => updateJob(selected.id, (job) => ({ ...job, reviewConfirmed: event.target.checked }))} />
        <span><strong>隠し残しがないことを確認しました</strong><small>画像の端や背景も見ました。</small></span>
      </label>

      {selected.error && <div className="inline-error" role="alert">{selected.error}</div>}
      {selected.metadataCheck?.clean && <div className="metadata-ok"><strong>位置情報などを削除しました</strong><span>保存する画像にEXIF・GPS・XMPは残っていません。</span></div>}

      <div className={`save-actions ${isAppleMobile ? 'is-apple-mobile' : 'is-standard-device'}`}>
        {isAppleMobile ? (
          <>
            <button
              className="save-button"
              type="button"
              disabled={!selected.reviewConfirmed}
              onClick={previewCurrent}
            >
              完成画像を表示
            </button>
            <p className="save-device-note">
              表示後に長押し、共有メニュー、緊急保存を選べます。
            </p>
          </>
        ) : (
          <>
            <button
              className="save-button"
              type="button"
              disabled={!selected.reviewConfirmed}
              onClick={saveCurrent}
            >
              画像を保存
            </button>
            <button
              className="share-button"
              type="button"
              disabled={!selected.reviewConfirmed}
              onClick={previewCurrent}
            >
              完成画像を確認
            </button>
          </>
        )}
      </div>
    </section>
  ) : null

  const renderWarnings = () => selected && (selected.warnings?.length ?? 0) > 0
    ? <div className="warning-card"><strong>一部の自動検出を完了できませんでした</strong>{selected.warnings?.map((warning) => <p key={warning}>{warning}</p>)}</div>
    : null

  return (
    <div className={`app-shell ${jobs.length ? 'has-work' : 'is-start'}`}>
      <header className="app-bar">
        <a className="brand" href="./" aria-label="かくして共有 ホーム">
          <span className="brand-mark" aria-hidden="true"><img src="./icons/icon.svg" alt="" /></span>
          <span><strong>かくして共有</strong><small>画像の情報を隠す</small></span>
        </a>
        <span className="header-free-note">無料・登録不要</span>
      </header>

      {globalMessage && <div className="global-message" role="status"><span>{globalMessage}</span><button type="button" onClick={() => setGlobalMessage(undefined)}>閉じる</button></div>}

      {savePreview && (
        <div
          className="save-preview-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="save-preview-title"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setSavePreview(undefined)
            }
          }}
        >
          <section className="save-preview-dialog">
            <header className="save-preview-header">
              <div>
                <span>完成画像</span>
                <strong id="save-preview-title">保存方法を選ぶ</strong>
              </div>
              <button
                type="button"
                onClick={() => setSavePreview(undefined)}
                aria-label="完成画像を閉じる"
              >
                閉じる
              </button>
            </header>

            {isAppleMobile ? (
              <div className="save-preview-guide">
                <strong>画像を長押ししてください</strong>
                <span>
                  表示されたメニューの「共有」から「画像を保存」を選択し、編集画像を写真アプリへ保存してください。
                </span>
              </div>
            ) : (
              <div className="save-preview-guide">
                <strong>完成画像を確認できます</strong>
                <span>問題なければ下の「画像を保存」を押してください。</span>
              </div>
            )}

            <div className="save-preview-image-area">
              <img src={savePreview.url} alt="加工済みの完成画像" />
            </div>

            <div className="save-preview-actions">
              {isAppleMobile ? (
                <>
                  {runtime.shareFiles && (
                    <button
                      className="save-button"
                      type="button"
                      onClick={sharePreviewCurrent}
                    >
                      保存
                    </button>
                  )}
                  <button
                    className={runtime.shareFiles ? 'share-button' : 'save-button'}
                    type="button"
                    onClick={savePreviewFile}
                  >
                    ファイルとして保存
                  </button>
                </>
              ) : (
                <button
                  className="save-button"
                  type="button"
                  onClick={savePreviewFile}
                >
                  画像を保存
                </button>
              )}

              <button
                className="preview-close-button"
                type="button"
                onClick={() => setSavePreview(undefined)}
              >
                編集画面へ戻る
              </button>
              <button
                className="preview-close-button"
                type="button"
                onClick={() => window.location.assign('./')}
              >
                トップに戻る
              </button>
            </div>

            {isAppleMobile && (
              <p className="save-preview-note">
                「保存」を押すと共有メニューが開きます。共有メニューから「画像を保存」を選択してください。
              </p>
            )}
          </section>
        </div>
      )}


      {jobs.length === 0 ? (
        <main className="start-screen">
          <section className="start-stage" aria-labelledby="start-title">
            <div className="start-copy">
              <p>かくして共有</p>
              <h1 id="start-title">共有する写真を選ぶ</h1>
              <span>顔や住所など、隠したい場所を確認して保存できます。</span>
            </div>
            <UploadZone onFiles={addFiles} />
            <details className="start-help" id="guide">
              <summary>使い方と安全性</summary>
              <div>
                <ol>
                  <li><strong>写真を選ぶ</strong><span>顔や文字など、隠した方がよい場所を自動で探します。</span></li>
                  <li><strong>場所を確認する</strong><span>足りない場所は追加し、不要な場所は外せます。</span></li>
                  <li><strong>保存する</strong><span>元の写真は上書きせず、新しい画像として保存します。</span></li>
                </ol>
                <p>画像の読み込み・検出・加工はブラウザ内で行い、運営者のサーバーへ送信しません。保存時に位置情報なども取り除きます。</p>
              </div>
            </details>
          </section>
<LegalDetails />
        </main>
      ) : (
        <main className={`studio ${jobs.length > 1 ? 'has-assets' : 'single-image'}`}>
          {!isMobile && jobs.length > 1 && <QueueSidebar jobs={jobs} selectedId={selectedId} onSelect={setSelectedId} onRemove={removeJob} onFiles={addFiles} />}

          <section className="studio-shell">
            {selected ? (
              <>
                <div className="studio-topbar">
                  {isMobile && <button className="mobile-image-switcher" type="button" onClick={() => setMobilePanel('assets')} aria-label={`画像一覧を開く（${selectedIndex + 1}/${jobs.length}）`} title="画像一覧"><ToolbarIcon name="images" /><strong>{selectedIndex + 1}/{jobs.length}</strong></button>}
                  <div className={`file-identity ${selected.phase === 'done' ? 'is-saved' : processing ? 'is-processing' : 'is-editing'}`} aria-label={`状態: ${selected.phase === 'done' ? '保存済み' : processing ? '検出中' : '編集中'}`}>
                    <span className="file-status-dot" aria-hidden="true" />
                    <h1 title={selected.name}>{selected.name}</h1>
                  </div>
                  <div className="history-tools" aria-label="編集操作">
                    <button type="button" disabled={selected.historyIndex <= 0} onClick={undo} aria-label="元に戻す" title="元に戻す"><ToolbarIcon name="undo" /></button>
                    <button type="button" disabled={selected.historyIndex >= selected.history.length - 1} onClick={redo} aria-label="やり直す" title="やり直す"><ToolbarIcon name="redo" /></button>
                    {!isMobile && jobs.length > 1 && <button className="zip-button" type="button" onClick={saveBatch} aria-label="確認済みをZIP保存" title="確認済みをZIP保存"><ToolbarIcon name="download" /><span>ZIP</span></button>}
                  </div>
                </div>

                {processing && (
                  <section className="processing-stage" role="status">
                    <div className="processing-field" aria-hidden="true"><span /></div>
                    <div className="processing-copy">
                      <span>{Math.round(selected.progress * 100)}%</span>
                      <h2>{selected.phaseLabel}</h2>
                      <p>見つけた場所を順番に確認しています。</p>
                      <progress value={selected.progress} max="1" />
                      {selected.decoded && ['face', 'ocr', 'barcode'].includes(selected.phase) && (
                        <button type="button" disabled={manualReviewIds.current.has(selected.id)} onClick={requestManualReview}>{manualReviewIds.current.has(selected.id) ? '手動確認への切替を待っています' : '自動検出を終了して手動で確認する'}</button>
                      )}
                    </div>
                  </section>
                )}

                {selected.phase === 'error' && (
                  <section className="error-stage" role="alert">
                    <span>処理を続けられませんでした</span>
                    <h2>画像を処理できませんでした</h2>
                    <p>{selected.error}</p>
                    <p>別形式へ変換するか、画像サイズを小さくして再度お試しください。</p>
                  </section>
                )}

                {selected.decoded && ['review', 'done'].includes(selected.phase) && (
                  <>
                    <div className="studio-grid">
                      <section className="canvas-pane">
                        {isMobile && (
                          <div className="mobile-review-strip">
                            <span><strong>{enabledRegionCount}か所見つかりました</strong><small>隠す場所を確認してください。</small></span>
                            <button type="button" onClick={() => { updateJob(selected.id, (job) => ({ ...job, selectedRegionId: undefined })); setMobilePanel('regions') }}>場所を見る</button>
                          </div>
                        )}

                        {!isMobile && (
                          <div className="canvas-toolbar">
                            <div className="detection-summary">
                              <strong>{enabledRegionCount}</strong><span>か所を隠します</span>
                            </div>
                            <div className="canvas-tools">
                              <button className={manualMode ? 'is-active' : ''} type="button" onClick={() => setManualMode((value) => !value)}>{manualMode ? '追加を終える' : '＋ 隠す場所を追加'}</button>
                              <button className={showOriginal ? 'is-active' : ''} type="button" onClick={() => setShowOriginal((value) => !value)}>{showOriginal ? '隠した画像に戻す' : '隠す前を見る'}</button>
                              <label className="zoom-control"><span>表示</span><input type="range" min="70" max="220" value={zoom} onChange={(event) => setZoom(Number(event.target.value))} /><output>{zoom}%</output></label>
                            </div>
                          </div>
                        )}

                        <div className="canvas-frame">
                          <EditorCanvas
                            decoded={selected.decoded}
                            regions={selected.regions}
                            selectedId={selected.selectedRegionId}
                            manualMode={manualMode}
                            zoom={zoom}
                            options={exportOptions}
                            showOriginal={showOriginal}
                            onSelect={(id) => updateJob(selected.id, (job) => ({ ...job, selectedRegionId: id }))}
                            onCommit={commitRegions}
                          />
                          {isMobile && (
                            <div className="mobile-zoom-controls" aria-label="表示倍率">
                              <button type="button" aria-label="縮小" onClick={() => setZoom((value) => Math.max(70, value - 20))}>−</button>
                              <output>{zoom}%</output>
                              <button type="button" aria-label="拡大" onClick={() => setZoom((value) => Math.min(220, value + 20))}>＋</button>
                            </div>
                          )}
                        </div>

                        {!isMobile && (
                          <div className="canvas-footnote">
                            <span className={showOriginal ? 'original-state' : 'edited-state'}>{showOriginal ? '隠す前の画像' : '隠した画像'}</span>
                            <span>場所を押すと、位置や大きさを変えられます。</span>
                          </div>
                        )}
                      </section>

                      {!isMobile && (
                        <aside className="inspector-pane">
                          <div className="review-notice"><strong>見つけた場所を確認</strong><span>足りない場所は、画像上で追加できます。</span></div>
                          {renderWarnings()}
                          <RegionList
                            regions={selected.regions}
                            selectedId={selected.selectedRegionId}
                            defaultMode={exportOptions.mode}
                            onSelect={(id) => updateJob(selected.id, (job) => ({ ...job, selectedRegionId: id }))}
                            onChange={commitRegions}
                          />
                          <div className="desktop-finish-bar">
                            <span><strong>{enabledRegionCount}か所を隠します</strong><small>最後に画像全体を見て保存します。</small></span>
                            <button type="button" onClick={() => setShowFinish(true)}>保存前に確認</button>
                          </div>
                        </aside>
                      )}
                    </div>

                    {isMobile && (
                      <>
                        <nav className="mobile-actionbar" aria-label="画像編集操作">
                          <button type="button" onClick={() => { updateJob(selected.id, (job) => ({ ...job, selectedRegionId: undefined })); setMobilePanel('regions') }} aria-label={`見つけた場所を確認（${enabledRegionCount}か所）`}><span>見つけた場所</span><small>{enabledRegionCount}か所</small></button>
                          <button className={manualMode ? 'is-active' : ''} type="button" aria-label={manualMode ? '追加を終える' : '隠す場所を追加'} onClick={() => setManualMode((value) => !value)}><span>{manualMode ? '追加を終える' : '場所を追加'}</span><small>{manualMode ? '画像を確認' : '指で囲む'}</small></button>
                          <button className={showOriginal ? 'is-active' : ''} type="button" aria-label={showOriginal ? '隠した画像に戻す' : '隠す前を見る'} onClick={() => setShowOriginal((value) => !value)}><span>{showOriginal ? '隠した画像' : '隠す前'}</span><small>{showOriginal ? '表示を戻す' : '押して比較'}</small></button>
                          <button className="primary" type="button" aria-label="保存前の確認へ" onClick={() => setMobilePanel('finish')}><span>保存する</span><small>最後に確認</small></button>
                        </nav>

                        {mobilePanel && (
                          <div className="mobile-sheet-layer">
                            <button className="mobile-sheet-backdrop" type="button" aria-label="パネルを閉じる" onClick={() => setMobilePanel(null)} />
                            <section className={`mobile-sheet panel-${mobilePanel}`} role="dialog" aria-modal="true" aria-label={mobilePanel === 'assets' ? '画像一覧' : mobilePanel === 'regions' ? '見つけた場所' : '保存前の確認'}>
                              <div className="mobile-sheet-handle" aria-hidden="true" />
                              <header className="mobile-sheet-header">
                                <div><span>{mobilePanel === 'assets' ? '画像' : mobilePanel === 'regions' ? '見つけた場所' : '保存'}</span><h2>{mobilePanel === 'assets' ? `${jobs.length}枚の画像` : mobilePanel === 'regions' ? `${enabledRegionCount}か所見つかりました` : '保存前の確認'}</h2></div>
                                <button type="button" onClick={() => setMobilePanel(null)}>{mobilePanel === 'regions' ? '画像へ戻る' : '閉じる'}</button>
                              </header>
                              <div className="mobile-sheet-body">
                                {mobilePanel === 'assets' && <QueueSidebar jobs={jobs} selectedId={selectedId} onSelect={(id) => { setSelectedId(id); setMobilePanel(null) }} onRemove={removeJob} onFiles={addFiles} />}
                                {mobilePanel === 'regions' && (
                                  <>
                                    <div className="review-notice"><strong>隠す場所を選んでください</strong><span>不要な場所は「隠さない」にできます。</span></div>
                                    {renderWarnings()}
                                    <RegionList
                                      regions={selected.regions}
                                      selectedId={selected.selectedRegionId}
                                      defaultMode={exportOptions.mode}
                                      onSelect={(id) => updateJob(selected.id, (job) => ({ ...job, selectedRegionId: id }))}
                                      onChange={commitRegions}
                                    />
                                  </>
                                )}
                                {mobilePanel === 'finish' && renderFinishPanel(true)}
                              </div>
                            </section>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            ) : <div className="empty-work">画像を選択してください。</div>}
          </section>
        </main>
      )}

      {showFinish && selected && !isMobile && (
        <div className="finish-dialog-layer" role="presentation">
          <button className="finish-dialog-backdrop" type="button" aria-label="保存前の確認を閉じる" onClick={() => setShowFinish(false)} />
          <section className="finish-dialog" role="dialog" aria-modal="true" aria-labelledby="finish-title">
            <header className="finish-dialog-header">
              <div><span>仕上げ</span><strong>保存前の確認</strong></div>
              <button type="button" onClick={() => setShowFinish(false)}>編集に戻る</button>
            </header>
            <div className="finish-dialog-body">{renderFinishPanel(false)}</div>
          </section>
        </div>
      )}
    </div>
  )

}

function LegalDetails() {
  return (
    <section className="legal-details">
      <details id="privacy"><summary>画像の扱い</summary><div><p>画像はブラウザ内で読み込み、検出、加工、再生成します。運営者のサーバーへ画像を送信・保存する機能はありません。</p><p>顔検出モデル、日本語OCR辞書、WASM実行ファイル等は、このWebサイトと同じ配信元から取得され、ブラウザにキャッシュされる場合があります。アクセス解析、広告、外部CDN、外部APIは使用しません。</p><p>端末やブラウザの標準機能により、一時ファイル、キャッシュ、ダウンロード履歴等が端末内に残る場合があります。共有先へ送信した後の取り扱いは、共有先のサービスに従います。</p></div></details>
      <details id="terms"><summary>利用規約・免責の要点</summary><div><p>本サービスは、画像内の個人情報候補の確認と加工を支援するものであり、検出漏れがないこと、匿名化が完全であること、特定目的への適合性を保証しません。</p><p>保存前に画像全体を利用者自身で確認してください。特に、子ども、医療・福祉、学校、顧客、従業員等の画像は、撮影・利用・共有に必要な同意や権限を確認してください。</p><p>重要情報には黒塗りを使用してください。ぼかし・モザイクは視認性を下げますが、復元や推測の可能性を完全には排除できません。</p></div></details>
      <details id="licenses"><summary>OSSライセンス</summary><div><p>本体コードはMIT Licenseです。YuNetモデルはMIT、ONNX Runtime WebはMIT、Tesseract.jsおよび日本語学習データはApache-2.0、ZXing関連はMIT・Apache-2.0・BSD-3-Clauseです。</p><p>HEIC/HEIF変換にはLGPL-3.0のheic-to/libheifを独立した依存関係として利用します。配布時は<a href="./licenses/THIRD_PARTY_NOTICES.txt">第三者ライセンス通知</a>と各ライセンス原文を確認してください。</p></div></details>
    </section>
  )
}
